// july25 SidePanel with scrollable palette
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { Box, Typography, Accordion, AccordionSummary, AccordionDetails } from '@mui/material';
import ArrowForwardIosSharpIcon from '@mui/icons-material/ArrowForwardIosSharp';
import { useLocation } from 'react-router-dom'; // 🆕 used to scope behavior to design/layout only
import FieldSettings from './FormDesignLayout/FieldSettings';
import { useFieldEdit } from '../../contexts/FieldEditContext';
import { useFormLayout } from '../../contexts/FormLayoutContext';
import { safeGetItem, safeSetItem } from '../../utils/storageUtils'; // 🆕 adjust path if needed

const SidePanel = ({
  sections,
  onBack,
  onSave,
  initialTab,
  fieldDependencyData,
  dropdownOptionsOverride = null,
}) => {
  const [expanded, setExpanded] = useState({});
  const [hasHydratedExpanded, setHasHydratedExpanded] = useState(false); // 🆕 prevent overwriting storage on first render
  const location = useLocation(); // 🆕
  const { selectedFieldForEdit, clearSelectedField } = useFieldEdit();
  const { selectField } = useFormLayout(); // Removed unused `selectedField`

  // 🆕 Only enable accordion persistence behavior on Design/Layout pages
  const isAccordionPersistenceEnabled = useMemo(() => {
    const path = location?.pathname?.split('/').pop();
    return path === 'design' || path === 'layout';
  }, [location?.pathname]);

  // 🆕 Derive formId from the URL for a stable per-form key (no need to touch other contexts)
  const formIdFromPath = useMemo(() => {
    try {
      const parts = (location?.pathname || '').split('/').filter(Boolean);
      const fbIndex = parts.indexOf('form-builder');
      return fbIndex >= 0 && parts[fbIndex + 1] ? parts[fbIndex + 1] : 'unknownForm';
    } catch (e) {
      return 'unknownForm';
    }
  }, [location?.pathname]);

  // 🆕 Storage key is per-form + per-page-area (design/layout)
  const expandedStorageKey = useMemo(() => {
    const page = location?.pathname?.split('/').pop() || 'unknown';
    return `sidepanel_expanded_${formIdFromPath}_${page}`;
  }, [formIdFromPath, location?.pathname]);

  // Avoid re-rendering if metadata is unchanged
  const isValidField = useCallback(() => {
    return selectedFieldForEdit && selectedFieldForEdit.metadata;
  }, [selectedFieldForEdit]);

  // Log selectedFieldForEdit each time it changes to verify its state
  useEffect(() => {
    if (selectedFieldForEdit) {
      // console.log('[SidePanel: debug] Rendering FieldSettings with selectedFieldForEdit:', selectedFieldForEdit);
    } else {
      // console.log('[SidePanel: debug] No selectedFieldForEdit available, rendering fallback content.');
    }
  }, [selectedFieldForEdit]);

  useEffect(() => {
    if (!selectedFieldForEdit) {
      selectField(null); // Clear selected field in FormLayoutContext when no field is selected
    }
  }, [selectedFieldForEdit, selectField]);

  // 🆕 Hydrate expanded state (Design/Layout only), then:
  //     - If no saved state exists, open ALL sections by default
  //     - If saved state exists, DO NOT re-open sections the user collapsed
  //     - If new sections appear (not previously seen), open those by default
  useEffect(() => {
    if (!isAccordionPersistenceEnabled) return;

    // sections could be undefined briefly depending on parent render timing
    if (!Array.isArray(sections) || sections.length === 0) {
      return;
    }

    // Build a map of current panels (only those that render an Accordion)
    const currentPanelKeys = [];
    sections.forEach((section, index) => {
      if (!section?.noAccordion) {
        currentPanelKeys.push(`panel${index}`);
      }
    });

    const saved = safeGetItem(expandedStorageKey);

    // Normalize saved -> object map if possible
    const savedMap =
      saved && typeof saved === 'object' && !Array.isArray(saved)
        ? saved
        : null;

    // Start from saved (if any), otherwise start empty
    const nextExpanded = savedMap ? { ...savedMap } : {};

    // Ensure any new panels open by default (but don't override existing user choices)
    currentPanelKeys.forEach((panelKey) => {
      if (typeof nextExpanded[panelKey] !== 'boolean') {
        nextExpanded[panelKey] = true;
      }
    });

    // Optional cleanup: remove panels that no longer exist
    Object.keys(nextExpanded).forEach((panelKey) => {
      if (!currentPanelKeys.includes(panelKey)) {
        delete nextExpanded[panelKey];
      }
    });

    setExpanded(nextExpanded);
    setHasHydratedExpanded(true);
  }, [sections, expandedStorageKey, isAccordionPersistenceEnabled]);

  // 🆕 Persist expanded state whenever it changes (Design/Layout only, after hydration)
  useEffect(() => {
    if (!isAccordionPersistenceEnabled) return;
    if (!hasHydratedExpanded) return;
    safeSetItem(expandedStorageKey, expanded);
  }, [expanded, expandedStorageKey, hasHydratedExpanded, isAccordionPersistenceEnabled]);

  const handleChange = (panel) => (event, isExpanded) => {
    setExpanded((prev) => ({
      ...prev,
      [panel]: isExpanded ? true : false,
    }));
  };

  const handleNavigateBack = () => {
    clearSelectedField(); // Clear field selection on back navigation
    onBack();
  };

  return (
    /*──────────── Outer wrapper ────────────
      • Fills the builder column exactly (100 % of its parent)
      • FieldSettings scrolls via inner sticky box (as before)
      • Palette now has its *own* scroll container
    */
    <Box
      sx={{
        minWidth: '30%',
        maxWidth: '30%',
        bgcolor: 'background.paper',
        height: '100%', // parent is constrained in FormLayout / builder pages
        p: 0,
      }}
    >
      {isValidField() ? (
        /*────────── Sticky inner box ──────────
          • Sticks to top of viewport
          • Single scrollbar that reaches full panel height
        */
        <Box
          sx={{
            position: 'sticky',
            top: 0,
            maxHeight: '100%',
            overflowY: 'auto',
            pb: 2,              // extra padding at bottom so last control isn't cut off
          }}
        >
          <FieldSettings
            onSave={onSave}
            onNavigateBack={handleNavigateBack}
            selectedField={selectedFieldForEdit}
            initialTab={initialTab}
            fieldDependencyData={fieldDependencyData}
            dropdownOptionsOverride={dropdownOptionsOverride}
          />
        </Box>
      ) : (
        // 👇 scrollable container around the palette
        <Box
          sx={{
            maxHeight: '100%',
            overflowY: 'auto',
            pb: 2,              // 🆕 extra padding at bottom for all palette sections
          }}
        >
          {sections.map((section, index) => {
            return section.noAccordion ? (
              <Box key={index} sx={{ p: section.noPadding ? 0 : 2 }}>
                {section.items.map((item, idx) => (
                  <Box
                    key={idx}
                    sx={{ width: '100%', p: section.noPadding ? 0 : '4px 8px' }}
                  >
                    {item.element}
                  </Box>
                ))}
              </Box>
            ) : (
              <Accordion
                key={index}
                expanded={expanded[`panel${index}`] || false}
                onChange={handleChange(`panel${index}`)}
                disableGutters
                square
                sx={{
                  m: 0,
                  borderBottom: 'none',
                  '&:before': { display: 'none' }, // remove default divider line
                }}
              >
                <AccordionSummary
                  expandIcon={
                    <ArrowForwardIosSharpIcon
                      sx={{
                        fontSize: '0.8rem',
                        transform: `rotate(${expanded[`panel${index}`] ? '-90deg' : '0deg'})`,
                      }}
                    />
                  }
                  sx={{
                    flexDirection: 'row-reverse',
                    justifyContent: 'space-between',
                    m: 0,
                    py: 1,
                    px: 1,

                    // ↓ Reduce default heights
                    minHeight: 32,
                    '&.Mui-expanded': {
                      minHeight: 32,
                    },

                    // ↓ Kill extra margin in both normal & expanded states
                    '& .MuiAccordionSummary-content': {
                      margin: 0,
                    },
                    '& .MuiAccordionSummary-content.Mui-expanded': {
                      margin: 0,
                    },
                  }}
                >
                  <Typography
                    sx={{ fontSize: '0.8rem', fontWeight: 'bold', p: '0px 16px' }}
                  >
                    {section.title}
                  </Typography>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    p: 0,
                    display: 'flex',
                    flexDirection: 'column',
                    '& > :last-child': {
                      pb: 2,   // we can keep this too; doesn't hurt
                    },
                  }}
                >
                  {section.items.map((item, idx) => (
                    <Box
                      key={idx}
                      sx={{
                        width: '100%',
                        p: '4px 8px',
                        borderBottom: '0px solid #e0e0e0',
                      }}
                    >
                      {item.element}
                    </Box>
                  ))}
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default SidePanel;
